import { useState } from 'react'
import '../styles/Contact.css'

const FAQS = [
  { q: 'What is the minimum order quantity?', a: 'We accept orders from a single piece. For bulk orders (50+), we offer special corporate discounts.' },
  { q: 'How long does delivery take?', a: 'Standard orders take 3–5 working days. Express same-day service is available for urgent requirements in Chennai.' },
  { q: 'Can I upload my own logo?', a: 'Yes! Share your logo (PDF/AI/PNG) and our design team will prepare a free digital mock-up for approval.' },
  { q: 'Do you provide GST invoices?', a: 'Yes, we are a GST-registered business and provide full tax invoices for all orders.' },
]

// ─── Custom Popup Component ───────────────────────────────────────────────────
function Popup({ type, onClose }) {
  if (!type) return null

  const isSuccess = type === 'success'

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div
        className={`popup-card ${isSuccess ? 'popup-success' : 'popup-error'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <span className="popup-corner tl" />
        <span className="popup-corner tr" />
        <span className="popup-corner bl" />
        <span className="popup-corner br" />

        <div className="popup-icon-wrap">
          {isSuccess ? (
            <svg viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="26" cy="26" r="25" stroke="#d4af37" strokeWidth="2" />
              <path d="M14 27l8 8 16-16" stroke="#d4af37" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <svg viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="26" cy="26" r="25" stroke="#e05252" strokeWidth="2" />
              <path d="M18 18l16 16M34 18L18 34" stroke="#e05252" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          )}
        </div>

        <p className="popup-label">{isSuccess ? 'Inquiry Received' : 'Submission Failed'}</p>
        <h2 className="popup-title">{isSuccess ? 'Thank You!' : 'Oops!'}</h2>
        <p className="popup-body">
          {isSuccess
            ? 'Your inquiry has been submitted successfully. Our team will reach out within 4 hours.'
            : 'Something went wrong while sending your message. Please try again or call us directly.'}
        </p>

        <button className="popup-btn" onClick={onClose}>
          {isSuccess ? 'Got it →' : 'Try Again'}
        </button>
      </div>
    </div>
  )
}

// ─── Main Contact Page ────────────────────────────────────────────────────────
export default function Contact() {
  const [openFaq, setOpenFaq] = useState(null)
  const [form, setForm] = useState({ name: '', phone: '', email: '', service: 'Custom Trophies', message: '' })
  const [loading, setLoading] = useState(false)
  const [popup, setPopup] = useState(null)

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      const response = await fetch('http://127.0.0.1:8000/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })

      if (response.ok) {
        setPopup('success')
        setForm({ name: '', phone: '', email: '', service: 'Custom Trophies', message: '' })
      } else {
        setPopup('error')
      }
    } catch (err) {
      console.error(err)
      setPopup('error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="contact-page light-theme">

      {/* ── Custom Popup ── */}
      <Popup type={popup} onClose={() => setPopup(null)} />

      {/* ── Page Hero ── */}
      <div className="page-hero-mini">
        <div className="breadcrumb">
          <span className="gold">Contact Support</span>
        </div>
        <h1 className="hero-title">Get in <span className="gold-grad">Touch</span></h1>
        <p className="hero-subtitle">Ready to recognize excellence? We are here to help.</p>
      </div>

      {/* ── Main Contact Grid ── */}
      <section className="section">
        <div className="contact-main-grid">

          {/* Information Sidebar */}
          <div className="contact-sidebar">
            <div className="sidebar-inner">
              <span className="sec-tag">Official Studio</span>
              <h3>Super Laser <span className="gold">India</span></h3>
              <p className="sidebar-desc">Visit our manufacturing facility in the heart of Chennai.</p>

              <div className="contact-methods">
                <div className="cm-item">
                  <div className="cm-icon-circle">📍</div>
                  <div className="cm-text">
                    <strong>Address</strong>
                    <p>No.36, M, B street, Ellis Rd, Mount Road, Padupakkam, Chennai, Tamil Nadu 600002</p>
                  </div>
                </div>
                <div className="cm-item">
                  <div className="cm-icon-circle">📞</div>
                  <div className="cm-text">
                    <strong>Phone</strong>
                    <a href="tel:+919840538852">+91 98405 38852 - Khetan Singh</a><br />
                    <a href="tel:+919940334044">+91 99403 34044 - Hitesh</a><br />

                  </div>
                </div>
                <div className="cm-item">
                  <div className="cm-icon-circle">📧</div>
                  <div className="cm-text">
                    <strong>Email</strong>
                    <a href="mailto:superlaserindiasli@gmail.com">superlaserindia9@gmail.com</a>
                  </div>
                </div>
              </div>

              <div className="social-row">
                {['fb', 'ig', 'li', 'wa'].map(s => <div key={s} className="soc-dot"></div>)}
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="contact-form-wrapper">
            <div className="form-glass-card">
              <h3>Start Your <span className="gold-grad">Order</span></h3>
              <p>Briefly describe your requirements and we'll reply within 4 hours.</p>

              <form className="luxury-form" onSubmit={handleSubmit}>
                <div className="input-row">
                  <div className="input-group">
                    <label>Full Name</label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div className="input-group">
                    <label>Phone Number</label>
                    <input
                      type="text"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="+91 00000 00000"
                      required
                    />
                  </div>
                </div>

                <div className="input-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="john@company.com"
                    required
                  />
                </div>

                <div className="input-group">
                  <label>Service Type</label>
                  <select name="service" value={form.service} onChange={handleChange}>
                    <option>Custom Trophies</option>
                    <option>Acrylic Awards</option>
                    <option>Crystal Plaques</option>
                    <option>Other / Custom</option>
                  </select>
                </div>

                <div className="input-group">
                  <label>Message / Requirements</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Quantity, Event Date, Design details..."
                  />
                </div>

                <button type="submit" className="form-submit-btn" disabled={loading}>
                  {loading ? (
                    <span className="btn-loading">
                      <span className="spinner" /> Sending...
                    </span>
                  ) : (
                    'Submit Inquiry →'
                  )}
                </button>
              </form>
            </div>
          </div>

        </div>
      </section>

      {/* ── Map Section ── */}
      <section className="section map-section">
        <div className="map-section-inner">
          <div className="map-header">
            <span className="sec-tag">Find Us</span>
            <h2 className="sec-title">Our <span className="gold-grad">Location</span></h2>
            <p className="map-subtitle">
              No.36, M, B street, Ellis Rd, Mount Road, Chennai – 600002
            </p>
          </div>

          <div className="map-wrapper">
            {/* Decorative border frame */}
            <span className="map-corner tl" />
            <span className="map-corner tr" />
            <span className="map-corner bl" />
            <span className="map-corner br" />

            <iframe
              title="Super Laser India Location"
              className="map-iframe"
              src="https://maps.google.com/maps?q=No.36,+M,+B+street,+Ellis+Rd,+Mount+Road,+Padupakkam,+Triplicane,+Chennai,+Tamil+Nadu+600002&t=&z=17&ie=UTF8&iwloc=&output=embed"
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          <a
            href="https://maps.google.com/?q=No.36,+M,+B+street,+Ellis+Rd,+Mount+Road,+Padupakkam,+Triplicane,+Chennai,+Tamil+Nadu+600002"
            target="_blank"
            rel="noopener noreferrer"
            className="map-directions-btn"
          >
            <span>📍</span> Get Directions in Google Maps
          </a>
        </div>
      </section>

      {/* ── FAQ Section ── */}
      <section className="section faq-dark">
        <div className="sec-header centered">
          <span className="sec-tag">Quick Help</span>
          <h2 className="sec-title">Common <span className="gold-grad">Questions</span></h2>
          <div className="title-underline"></div>
        </div>

        <div className="faq-container-new">
          {FAQS.map((f, i) => (
            <div key={i} className={`faq-lux-item ${openFaq === i ? 'active' : ''}`}>
              <div className="faq-lux-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                <span>{f.q}</span>
                <span className="faq-plus">{openFaq === i ? '−' : '+'}</span>
              </div>
              <div className="faq-lux-a">
                <p>{f.a}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}